import React from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Bath, Heart, Star, Award, PawPrint, ArrowRight, Sparkles } from 'lucide-react';
import './Home.css';

function Home() {
  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero__bg">
          <img src="/images/dog-portrait.jpg" alt="Beautiful groomed dog" />
          <div className="hero__overlay" />
        </div>
        <div className="hero__content container">
          <div className="hero__text">
            <span className="hero__badge">
              <Sparkles size={14} /> Abingdon, Oxfordshire
            </span>
            <h1 className="hero__title">
              Luxury Dog &amp; Cat<br />
              <span className="hero__title-accent">Grooming Salon &amp; Professional Academy</span>
            </h1>
            <p className="hero__subtitle">
              Tails &amp; Tweed is an established, family run, luxury dog and cat grooming salon
              and professional grooming academy, built on exceptional standards, experience,
              and animal welfare.
            </p>
            <div className="hero__actions">
              <a
                href="https://app.toplinedogs.co.uk/public/online-bookings?salon=YWJmZDE4NjAtMWMzZi00NjMyLTkyNTQtMDhkOWI2OGFiZGUx"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-accent"
              >
                Book Grooming
              </a>
              <Link to="/services" className="btn btn-white">
                Our Services <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
        <div className="hero__scroll-indicator">
          <div className="hero__scroll-line" />
        </div>
      </section>

      {/* Trust Badges */}
      <section className="trust">
        <div className="container">
          <div className="trust__grid">
            <div className="trust__item">
              <Award size={28} />
              <div>
                <h4>Master Groomer</h4>
                <p>International Certified</p>
              </div>
            </div>
            <div className="trust__item">
              <Star size={28} />
              <div>
                <h4>25+ Years</h4>
                <p>Industry Experience</p>
              </div>
            </div>
            <div className="trust__item">
              <Heart size={28} />
              <div>
                <h4>City &amp; Guilds</h4>
                <p>Qualified Team</p>
              </div>
            </div>
            <div className="trust__item">
              <PawPrint size={28} />
              <div>
                <h4>First Aid</h4>
                <p>Trained &amp; Certified</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="about section">
        <div className="container">
          <div className="about__grid">
            <div className="about__images">
              <div className="about__img-main">
                <img src="/images/salon.jpg" alt="Tails and Tweed Salon" />
              </div>
              <div className="about__img-accent">
                <img src="/images/three-dogs.jpg" alt="Happy groomed dogs" />
              </div>
              <div className="about__decoration" />
            </div>
            <div className="about__content">
              <span className="section-label">Welcome to Tails &amp; Tweed</span>
              <h2 className="section-title">A Luxury Grooming Experience in Abingdon</h2>
              <p className="about__text">
                With over 25 years of hands-on industry experience, advanced qualifications,
                and a reputation for excellence, we specialise in calm, considered grooming for
                pets including specialist services such as handstripping &mdash; and high-quality
                training for those serious about a career in the grooming industry.
              </p>
              <p className="about__text">
                Whether you are a discerning pet owner seeking expert care, or an aspiring groomer
                ready to train to professional standards, Tails &amp; Tweed offers an environment
                where quality, integrity, and expertise come first.
              </p>
              <Link to="/team" className="btn btn-outline">
                Meet Our Team <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview section" style={{ background: 'var(--color-cream)' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: 60 }}>
            <span className="section-label">What We Offer</span>
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">
              From full styling to relaxing spa treatments, we offer a complete range of
              grooming services for dogs, cats and small animals.
            </p>
          </div>

          <div className="services-preview__grid">
            <Link to="/services" className="service-card">
              <div className="service-card__icon">
                <Scissors size={32} />
              </div>
              <h3 className="service-card__title">Dog Grooming</h3>
              <p className="service-card__text">
                Full grooming, bath &amp; brush, hand stripping, and puppy introductions.
                Styling to breed standard or your requirements.
              </p>
              <span className="service-card__link">
                Learn More <ArrowRight size={14} />
              </span>
            </Link>

            <Link to="/cat-grooming" className="service-card">
              <div className="service-card__icon">
                <PawPrint size={32} />
              </div>
              <h3 className="service-card__title">Cat Grooming</h3>
              <p className="service-card__text">
                Specialist feline grooming with welfare at its core. Delivered with patience,
                experience, and a deep understanding of feline behaviour.
              </p>
              <span className="service-card__link">
                Learn More <ArrowRight size={14} />
              </span>
            </Link>

            <Link to="/spa-experiences" className="service-card">
              <div className="service-card__icon">
                <Sparkles size={32} />
              </div>
              <h3 className="service-card__title">Spa Experiences</h3>
              <p className="service-card__text">
                Luxury spa treatments including shed control, itch relief, signature facial,
                and our popular pawidcure.
              </p>
              <span className="service-card__link">
                Learn More <ArrowRight size={14} />
              </span>
            </Link>

            <Link to="/training" className="service-card">
              <div className="service-card__icon">
                <Award size={32} />
              </div>
              <h3 className="service-card__title">Groomer Training</h3>
              <p className="service-card__text">
                City &amp; Guilds accredited grooming courses. Level 2, Level 3,
                and combined pathways in our dedicated training academy.
              </p>
              <span className="service-card__link">
                Learn More <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section className="feature section">
        <div className="container">
          <div className="feature__grid">
            <div className="feature__content">
              <span className="section-label">The Tails &amp; Tweed Difference</span>
              <h2 className="section-title">Why Choose Us?</h2>
              <div className="feature__list">
                <div className="feature__item">
                  <div className="feature__item-icon">
                    <Award size={20} />
                  </div>
                  <div>
                    <h4>Highest Qualifications</h4>
                    <p>International Certified Master Groomer &amp; City &amp; Guilds qualified team with 25+ years experience.</p>
                  </div>
                </div>
                <div className="feature__item">
                  <div className="feature__item-icon">
                    <Heart size={20} />
                  </div>
                  <div>
                    <h4>Welfare-Led Approach</h4>
                    <p>Calm, structured and welfare focused. Every pet is groomed with time, attention and respect.</p>
                  </div>
                </div>
                <div className="feature__item">
                  <div className="feature__item-icon">
                    <Bath size={20} />
                  </div>
                  <div>
                    <h4>Specialist Services</h4>
                    <p>Recognised for expertise in traditional handstripping for both wire coats and gundog breeds.</p>
                  </div>
                </div>
                <div className="feature__item">
                  <div className="feature__item-icon">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <h4>Family Run</h4>
                    <p>An established, family run salon where quality, integrity, and expertise come first.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="feature__image">
              <img src="/images/corgi-bath.jpg" alt="Happy corgi getting groomed" />
              <div className="feature__image-decoration" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Band */}
      <section className="gallery-band">
        <div className="gallery-band__track">
          <img src="/images/dog-golden.jpg" alt="Golden retriever" />
          <img src="/images/three-dogs.jpg" alt="Three groomed dogs" />
          <img src="/images/corgi-bath.jpg" alt="Corgi bath" />
          <img src="/images/cat-lying.jpg" alt="Cat grooming" />
          <img src="/images/dog-puppy.jpg" alt="Puppy grooming" />
          <img src="/images/grooming-service.jpg" alt="Grooming service" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta__bg">
          <img src="/images/dog-golden.jpg" alt="" />
          <div className="cta__overlay" />
        </div>
        <div className="container cta__content">
          <span className="section-label" style={{ color: 'var(--color-accent-light)' }}>
            Ready to Book?
          </span>
          <h2 className="cta__title">Give Your Pet the Pampering They Deserve</h2>
          <p className="cta__text">
            Contact us today to book an appointment at our luxury Abingdon salon.
          </p>
          <div className="cta__actions">
            <a
              href="https://app.toplinedogs.co.uk/public/online-bookings?salon=YWJmZDE4NjAtMWMzZi00NjMyLTkyNTQtMDhkOWI2OGFiZGUx"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-accent"
            >
              Book Grooming
            </a>
            <a href="tel:07904813567" className="btn btn-white">
              Call 07904 813567
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
